import axios, { AxiosError } from "axios";
import { config } from "@/config";

/**
 * Shared HTTP client for the Perk Point API (api/ in this repo).
 *
 * In `vite dev` requests go to `/pp-api`, which vite.config.ts proxies to
 * VITE_PERKPOINT_API_URL (default http://localhost:3000). That keeps calls
 * same-origin, so they work in Codespaces and without CORS. Production builds
 * call VITE_PERKPOINT_API_URL directly.
 *
 * Auth is the shared admin token until staff login exists. The x-actor-*
 * headers let the audit log record who acted.
 */
export const perkpointApi = axios.create({
  baseURL: import.meta.env.DEV
    ? "/pp-api"
    : config.PERKPOINT_API_URL || "http://localhost:3000",
  headers: { "Content-Type": "application/json" },
});

function storedUser(): { name?: string; username?: string; email?: string } | null {
  try {
    const raw =
      sessionStorage.getItem("userInfo") || localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

perkpointApi.interceptors.request.use((request) => {
  request.headers.set(
    "Authorization",
    `Bearer ${config.PERKPOINT_ADMIN_TOKEN || "dev-admin-token"}`,
  );
  const user = storedUser();
  const name = user?.name || user?.username;
  const email = user?.email || (user?.username?.includes("@") ? user.username : "");
  if (name) request.headers.set("x-actor-name", name);
  if (email) request.headers.set("x-actor-email", email);
  return request;
});

export type ApiPage<T> = {
  items: T[];
  total: number;
  page: number;
  page_size: number;
};

type ApiErrorBody = {
  message?: string;
  errors?: Record<string, string>;
  error?: { code?: string; message?: string; details?: unknown };
};

/** Human message for a failed call. */
export function apiErrorMessage(error: unknown, fallback = "Something went wrong"): string {
  if (error instanceof AxiosError) {
    if (!error.response) return "Cannot reach the Perk Point API. Is it running?";
    const body = error.response.data as ApiErrorBody | undefined;
    const fields = body?.errors ? Object.values(body.errors) : [];
    return body?.error?.message || body?.message || fields[0] || fallback;
  }
  return error instanceof Error ? error.message : fallback;
}

/** `{ field: message }` from a 400/409 response, keyed by API field name. */
export function apiFieldErrors(error: unknown): Record<string, string> {
  if (error instanceof AxiosError) {
    const body = error.response?.data as ApiErrorBody | undefined;
    return body?.errors ?? {};
  }
  return {};
}

export function apiErrorCode(error: unknown): string | undefined {
  if (error instanceof AxiosError) {
    return (error.response?.data as ApiErrorBody | undefined)?.error?.code;
  }
  return undefined;
}

export function isNotFound(error: unknown) {
  return error instanceof AxiosError && error.response?.status === 404;
}
